// One followed opening's panel (Dashboard > Hiring, an opening's side panel): the three
// reads the server answers for it, by the link's id.
//
//   details     address, city, State, PIN, positions, where it is published, who raised it
//   team        recruiters, offer makers, schedulers, and each round's interviewer or client,
//               by name, as Recruitment's openings list names them
//   candidates  one page of the candidates - name, mobile number, the city they live in,
//               where the application came from, word, round, day applied (10 a page,
//               `nextOffset` for "Show more") - with the counts per word, for everyone who
//               may see hiring (the product owner's decision of 27 Oct 2026; until then the
//               rows went to a holder of "Raise & cancel hiring" only)
//
// The nine words and the round are the server's (hiring-opening.logic.ts): from the
// interview rounds it reads from 1Office's candidate-timeline (21 Oct 2026), or from the
// status when they could not be read. The browser never gets the rounds themselves.
//
// Who may open an opening at all is decided by the server from the verified caller; this
// file asks the same question for everyone and passes on what comes back. What a person
// reads is never put anywhere else by the web:
//   - a name or a mobile number is never in a URL: the candidates are always read with a
//     POST whose BODY carries the search (a name or a mobile number), the word and the page
//     (POST …/opening/candidates/search - a read; the server refuses a search in an address),
//     and the panel must not copy it into the page's own address;
//   - nothing here logs, and none of these calls is wrapped in product-analytics tracking.
//
// All three are quiet (`skipSessionExpiry`): the panel asks by itself, and a refusal must
// not end the session. The server answers 403, never 401, but a refusal is shown in the
// panel, not by signing the person out.
//
// The Hiring dashboard's Download (28 Oct 2026) is on the same router: one POST read of every
// opening on the tab and search on screen - all pages, not the twelve shown - with each one's
// details, team and candidates, which the page writes into one Excel file in the browser
// (components/hiring/hiringExport.ts). The tab and the search travel in its BODY, so a search
// is in no URL; it is quiet like the others, waits longer (the server waits up to 45 seconds
// for Recruitment), and nothing of it is logged or tracked.
import api from './api';
import type { HiringAddressText, HiringDashStatus, HiringStatus, HiringUnavailableReason } from './api';

/** The nine words a candidate's stage is shown in, in the panel's order. */
export const OPENING_STAGES = ['screened', 'scheduling', 'scheduled', 'interview', 'selected', 'offered', 'joined', 'rejected', 'withdrew'] as const;
export type OpeningStage = (typeof OPENING_STAGES)[number];

/** The words as the panel prints them. */
export const OPENING_STAGE_WORDS: Readonly<Record<OpeningStage, string>> = {
  screened: 'Screened',
  scheduling: 'Scheduling',
  scheduled: 'Scheduled',
  interview: 'Interview',
  selected: 'Selected',
  offered: 'Offered',
  joined: 'Joined',
  rejected: 'Rejected',
  withdrew: 'Withdrew',
};

/** What each word means, behind the hover. */
export const OPENING_STAGE_HINTS: Readonly<Record<OpeningStage, string>> = {
  screened: 'Put forward, waiting for approval',
  scheduling: 'Approved, interview not scheduled yet',
  scheduled: 'Interview scheduled',
  interview: 'A round passed, next not scheduled',
  selected: 'Through every round, no offer yet',
  offered: 'An offer is out',
  joined: 'Joined on this opening',
  rejected: 'Turned down, removed, or the opening closed',
  withdrew: 'Pulled out, did not join, or joined elsewhere',
};

/** Why something could not be read from Recruitment for the details. */
export type OpeningReadReason =
  | 'no_identity' | 'unreachable' | 'sign_in_failed' | 'timed_out' | 'busy'
  | 'malformed' | 'recruitment_error' | 'budget_not_in_project' | 'not_listed';

/** GET /hiring-links/:projectId/links/:linkId/opening/details - for everyone who may see hiring. */
export interface OpeningDetails {
  linkId: string;
  openingId: number;
  budgetId: number;
  /** The budget's name: the opening's designation ("Brand Promoter"). */
  budgetName: string;
  /** Null for a hiring with no store: show "No store". */
  store: { storeId: string; storeName: string | null; storeCode: string | null } | null;
  /** A hiring with no store: the label it was linked or raised under. Null for a store's. */
  roleLabel: string | null;
  /** Listed under its budget at this read; null when Recruitment could not be read. */
  listed: boolean | null;
  /** The four numbers (Positions, Filled, Offered, Open). */
  numbers: { positions: number; filled: number; offered: number; open: number } | null;
  /** `recruitment`: read just now; `last_seen`: kept from the last list that read them. */
  numbersFrom: 'recruitment' | 'last_seen' | null;
  address: { location: string | null; cityName: string | null; stateName: string | null; pincode: string | null };
  /** "ChannelPlay Jobs · Apna 03 Oct". `apnaOn` is a day, 'YYYY-MM-DD'. */
  published: { channelplayJobs: boolean | null; apnaOn: string | null };
  /** Raised from ChannelPlex, or linked by hand. 1Office does not say who created an opening. `on` is ISO. */
  created: { kind: 'raised' | 'linked'; on: string; byCode: string; byName: string | null };
  /** A shared opening: who else in the project follows it (for the (i)). */
  sharedWith: Array<{ storeName: string | null; storeCode: string | null; roleLabel: string | null }>;
  /** `readAt` (ISO) is when Recruitment was read, for the (i): "Read from Recruitment today at 10:42". */
  recruitment: { read: 'ok' | 'partial' | 'unavailable'; reason: OpeningReadReason | null; readAt: string };
}

/** One member of the team, by name, as Recruitment's openings list names them. */
export interface OpeningTeamPerson { name: string }

export type OpeningTeamRound =
  | { round: number; kind: 'interviewer'; name: string }
  /** A client round: show "Client". The name is null unless the person holds "Raise & cancel hiring". */
  | { round: number; kind: 'client'; name: string | null }
  | { round: number; kind: 'none' };

/** GET /hiring-links/:projectId/links/:linkId/opening/team - for everyone who may see hiring. */
export interface OpeningTeam {
  linkId: string;
  openingId: number;
  recruiters: OpeningTeamPerson[];
  offerMakers: OpeningTeamPerson[];
  schedulers: OpeningTeamPerson[];
  rounds: OpeningTeamRound[];
  /** Whether a client round's name is in this answer. */
  clientNamesShown: boolean;
  /** Whether the openings list carried the team's names; false: who is on it is not known. */
  namesRead: boolean;
  readAt: string;
}

/** How many candidates at each word. `other`: a status with no word (not shown as a word). `all`: everyone read. */
export interface OpeningStageCounts {
  screened: number;
  scheduling: number;
  scheduled: number;
  interview: number;
  selected: number;
  offered: number;
  joined: number;
  rejected: number;
  withdrew: number;
  other: number;
  all: number;
}

/** One row of the candidates table. Shown, never stored: not in a URL, a log, analytics or browser storage. Carries no id. */
export interface OpeningCandidateRow {
  name: string | null;
  /** Digits as 1Office holds them (with a leading + when it has one); null when it held no usable number. */
  mobile: string | null;
  /** The city the candidate lives in (their own current city in Recruitment, not the opening's); null shows "–". */
  from: string | null;
  /** Where the application came from, as 1Office words it ("Job Apply", "Recruiter"…): shown in short words (candidateSourceWord); null shows "–". */
  source: string | null;
  /** Null for a status ChannelPlex has no word for. */
  stage: OpeningStage | null;
  /** Joined on this opening and has since left it (the word is still Joined). */
  leftAfterJoining: boolean;
  /** 'YYYY-MM-DD'. */
  appliedOn: string | null;
  /** The round the word is about, where it is known; null shows "–". */
  round: number | null;
  /** That round was passed: "Round 1 done". */
  roundDone: boolean;
  /** The day that round is scheduled for ('YYYY-MM-DD'): "Round 3 · 21 Oct". */
  roundOn: string | null;
  /** The small grey line under the round. */
  roundNote: OpeningRoundNote | null;
}

export type OpeningRoundNote =
  /** More rounds are scheduled than the one shown: "+1 more". */
  | { kind: 'more'; count: number }
  /** The candidate did not come to the round shown: "No-show". */
  | { kind: 'no_show' }
  /** A round passed and the next one is not scheduled yet: "Round 2 to schedule". */
  | { kind: 'to_schedule'; round: number };

export interface OpeningCandidatePage {
  rows: OpeningCandidateRow[];
  /** How many match the search and the word, over every page ("10 of 28"). */
  total: number;
  offset: number;
  limit: number;
  /** Where "Show more" starts; null on the last page. */
  nextOffset: number | null;
}

/** POST /hiring-links/:projectId/links/:linkId/opening/candidates/search {q, stage, offset} - for everyone who may see hiring. */
export interface OpeningCandidates {
  linkId: string;
  openingId: number;
  /** The whole opening's, whatever the search: the number beside each word of the stage filter, and the tab's own. */
  counts: OpeningStageCounts;
  people: OpeningCandidatePage;
  readAt: string;
}

export interface OpeningCandidatesQuery {
  /** A name (letters, spaces, . ' -) or a mobile number (3 to 15 digits) to search for; at most 50 characters. Sent in the body only. The city and the source are not searched. */
  q?: string;
  /** One of the nine words; absent or 'all' for every word. */
  stage?: OpeningStage | 'all';
  /** Where the page starts: the previous answer's `nextOffset`. */
  offset?: number;
}

/* ─────────────────────────────── the Hiring dashboard's Download ─────────────────────────────── */

/** What a download is of: the dashboard's tab (absent or null for All) and its search (absent, null or blank for none). */
export interface HiringExportQuery {
  status?: HiringDashStatus | null;
  q?: string | null;
}

/**
 * What the file's Read column says of one opening:
 *   now           read from Recruitment for this download ("Recruitment now");
 *   last_read     Recruitment could not be read just now: the figures are the last good read's (`seenAt`; null if never);
 *   being_raised  Recruitment has not given it an opening yet;
 *   no_identity   the person has no 1Office sign-in to read with;
 *   deleted       a good read found the opening gone.
 */
export type HiringExportRead = 'now' | 'last_read' | 'being_raised' | 'no_identity' | 'deleted';

/**
 * One candidate row of the file, as everyone who may see hiring receives it: never an id, and
 * neither the city they live in nor a round read from their timeline (the product owner, 28 Oct
 * 2026). The word and the round are the STATUS's alone.
 */
export interface HiringExportCandidateRow {
  name: string | null;
  /** Compact digits with an optional leading +. Written into the file as TEXT, so leading digits and +91 survive. */
  mobile: string | null;
  /** 1Office's own word for where the application came from (candidateSourceWord puts it in short words). */
  source: string | null;
  /** Null for a status ChannelPlex has no word for. */
  stage: OpeningStage | null;
  /** Joined on this opening and has since left it: "Joined (left)". */
  leftAfterJoining: boolean;
  /** The round the status names; null when it names none. */
  round: number | null;
  /** That round was passed ("Round 2 done"). */
  roundDone: boolean;
  /** 'YYYY-MM-DD'. */
  appliedOn: string | null;
}

/**
 * One opening's candidates in a download:
 *   ok      read for this download: every row Recruitment lists for it, newest first (none is an answer);
 *   failed  its budget's candidates could not be read, or there is no 1Office sign-in: "Could not be read", NEVER 0;
 *   none    nothing to read: being raised, or deleted in Recruitment.
 */
export type HiringExportCandidates =
  | { read: 'ok'; rows: HiringExportCandidateRow[] }
  | { read: 'failed'; reason: OpeningReadReason }
  | { read: 'none' };

/**
 * One opening of a download: the dashboard's own row - the same fields, names and meanings as a
 * HiringLink of the list, without any of ChannelPlex's ids, so it is worded by the dashboard's
 * own helpers - then the panel's details and team, the Read column and the candidates.
 */
export interface HiringExportOpening {
  subject: { kind: 'store'; storeName: string | null; storeCode: string | null } | { kind: 'role'; roleLabel: string | null };
  budget: { name: string };
  /** Recruitment's own opening number; null while it is being raised. */
  openingId: number | null;
  shared: boolean;
  origin: string;
  reason: string;
  replacesEmpCode: string | null;
  state: 'sending' | 'live' | 'closed';
  status: HiringStatus;
  lastKnownStatus: HiringStatus | null;
  unavailableReason: HiringUnavailableReason | null;
  dashStatus: HiringDashStatus | 'closed';
  attention: string[];
  needsAttention: boolean;
  numbers: { positions: number; filled: number; offered: number; exiting: number; open: number } | null;
  numbersFrom: 'live' | 'last_seen' | null;
  /** When the figures shown were read (the "Last read" time). */
  seenAt: string | null;
  address: { linked: HiringAddressText | null; now: HiringAddressText | null };
  /** ISO: when the link last changed in ChannelPlex. */
  updatedAt: string;
  /** The PIN kept on the link when it was linked or raised. */
  pincode: string | null;
  /** Null when it was not read just now. `apnaOn` is 'YYYY-MM-DD'. */
  published: { channelplayJobs: boolean | null; apnaOn: string | null } | null;
  created: { kind: 'raised' | 'linked'; on: string; byCode: string; byName: string | null };
  /** Null when it was not read just now. A client round's name only when `clientNamesShown`. No schedulers. */
  team: { recruiters: OpeningTeamPerson[]; offerMakers: OpeningTeamPerson[]; rounds: OpeningTeamRound[] } | null;
  read: HiringExportRead;
  candidates: HiringExportCandidates;
}

/** POST /hiring-links/:projectId/export {status, q} - for everyone who may see hiring. Nothing is written or recorded. */
export interface HiringExport {
  /** The tab as applied (null: All). */
  status: HiringDashStatus | null;
  /** The search as applied, trimmed and single-spaced (null: none). */
  q: string | null;
  /** ISO: when Recruitment was read. */
  readAt: string;
  /** More than 500 openings matched: only the newest 500 are in. */
  capped: boolean;
  /** Whether a client round's name is in this answer (a holder of "Raise & cancel hiring", or a super admin). */
  clientNamesShown: boolean;
  /** `unread`: openings whose figures are the last read's, read with no sign-in, or whose candidates could not be read. */
  counts: { openings: number; candidates: number; unread: number };
  /** The dashboard's order: newest change first. */
  openings: HiringExportOpening[];
}

type HiringOk<T> = { success: true; data: T };
type QuietConfig = NonNullable<Parameters<typeof api.get>[1]> & { skipSessionExpiry: true };
/** The server waits for Recruitment up to 12 seconds; this leaves room for the rest. */
const OPENING_READ_TIMEOUT_MS = 30_000;
/**
 * The Download: the server waits up to 45 seconds for Recruitment, for every budget together;
 * this leaves room for the rest, and stays well under the platform's own limit of about 240 s.
 */
export const HIRING_EXPORT_TIMEOUT_MS = 60_000;
const quiet = (params: Record<string, unknown> | undefined, signal?: AbortSignal, timeout: number = OPENING_READ_TIMEOUT_MS): QuietConfig => ({
  ...(params ? { params } : {}),
  ...(signal ? { signal } : {}),
  timeout,
  skipSessionExpiry: true,
});
const base = (projectId: number, linkId: string): string =>
  `/hiring-links/${encodeURIComponent(String(projectId))}/links/${encodeURIComponent(linkId)}/opening`;

/** The body of the candidates read: only what says something - no empty `q`, no `stage: 'all'`, no `offset: 0`. */
const candidatesBody = (query: OpeningCandidatesQuery): Record<string, string | number> => {
  const body: Record<string, string | number> = {};
  const q = (query.q ?? '').trim();
  if (q) body.q = q;
  if (query.stage && query.stage !== 'all') body.stage = query.stage;
  if (query.offset && query.offset > 0) body.offset = query.offset;
  return body;
};

/** The body of the Download: only what says something - no status for All, no blank search. */
const exportBody = (query: HiringExportQuery): Record<string, string> => {
  const body: Record<string, string> = {};
  if (query.status) body.status = query.status;
  const q = (query.q ?? '').trim();
  if (q) body.q = q;
  return body;
};

export const hiringOpeningAPI = {
  details: (projectId: number, linkId: string, signal?: AbortSignal) =>
    api.get<HiringOk<OpeningDetails>>(`${base(projectId, linkId)}/details`, quiet(undefined, signal)),
  team: (projectId: number, linkId: string, signal?: AbortSignal) =>
    api.get<HiringOk<OpeningTeam>>(`${base(projectId, linkId)}/team`, quiet(undefined, signal)),
  /** Always a POST (a read): the search travels in the body, never in the address. */
  candidates: (projectId: number, linkId: string, query: OpeningCandidatesQuery = {}, signal?: AbortSignal) =>
    api.post<HiringOk<OpeningCandidates>>(`${base(projectId, linkId)}/candidates/search`, candidatesBody(query), quiet(undefined, signal)),
  /**
   * The Hiring dashboard's Download: every opening of the tab and search, with its details,
   * team and candidates. A POST that reads: the search travels in the body, never in the address.
   */
  exportHiring: (projectId: number, query: HiringExportQuery = {}, signal?: AbortSignal) =>
    api.post<HiringOk<HiringExport>>(
      `/hiring-links/${encodeURIComponent(String(projectId))}/export`,
      exportBody(query),
      quiet(undefined, signal, HIRING_EXPORT_TIMEOUT_MS),
    ),
};

/** A mobile number as the panel shows it: "90000 01234" for ten digits, otherwise as it came. */
export const showMobile = (mobile: string | null): string => {
  if (!mobile) return '–';
  const ten = /^(?:\+?91)?(\d{5})(\d{5})$/.exec(mobile);
  return ten ? `${ten[1]} ${ten[2]}` : mobile;
};
